import CSS from 'csstype';
import { debounce } from 'lodash-es';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { borderRadius, Div, fontSizing } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

type TextSize = 'extraSmall' | 'small';
const sizeStyles: Record<TextSize, CSS.Properties> = {
  extraSmall: {
    fontSize: fontSizing.extraSmall,
  },
  small: {
    fontSize: fontSizing.small,
  },
};

interface ITextareaProps {
  className?: string;
  fullWidth?: boolean;
  handleOnChange: (text: string) => void;
  isAutoFocused?: boolean;
  isDisabled?: boolean;
  isResizable?: boolean;
  initialValue?: string;
  maxLength?: number;
  minHeight?: number;
  minLength?: number;
  msDelay?: number; // add delay to debounce. default 0. recommended 300 or 500 if needing debounce
  placeholder?: string;
  size?: TextSize;
  style?: CSS.Properties;
  width?: CSS.WidthProperty<string | 0> | undefined;
}

export const Textarea = ({
  className = 'textarea-base',
  fullWidth,
  handleOnChange,
  isAutoFocused,
  isDisabled = false,
  isResizable = false,
  initialValue,
  maxLength = -1,
  minHeight = -1,
  minLength = 0,
  msDelay = 0,
  placeholder,
  size = 'small',
  style = {},
  width = '240px',
}: ITextareaProps) => {
  const [value, setValue] = useState(initialValue || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const debouncedOnChange = useMemo(() => {
    return debounce(handleOnChange, msDelay);
  }, [handleOnChange]);

  const onChange = (value: string) => {
    if (isDisabled) return;
    setValue(value);
    debouncedOnChange(value);
  };

  const onPaste = (
    e: React.ClipboardEvent<HTMLInputElement> | React.ClipboardEvent<HTMLTextAreaElement>,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    const clipboardData = e.clipboardData?.getData('Text') || '';
    const { selectionStart, selectionEnd, value } = e.currentTarget || {};
    if (selectionStart === selectionEnd) {
      if (selectionStart === 0) {
        onChange(clipboardData + value);
        return;
      }
      if ((selectionStart || 0) >= value.length) {
        onChange(value + clipboardData);
        return;
      }
    }
    onChange(
      value.substring(0, selectionStart || 0) +
        clipboardData +
        value.substring(selectionEnd || 0, value.length + 1),
    );
  };

  // handle auto resizing the textarea when its content changes
  useEffect(() => {
    if (textareaRef.current) {
      // We need to reset the height momentarily to get the correct scrollHeight for the textarea
      textareaRef.current.style.height = '0px';
      textareaRef.current.style.height =
        Math.max(minHeight, textareaRef.current.scrollHeight) + 'px';
    }
  }, [textareaRef.current, value]);

  return (
    <Div style={{ width: fullWidth ? '100%' : width }}>
      <textarea
        ref={textareaRef}
        rows={1}
        autoFocus={isAutoFocused}
        className={className}
        placeholder={placeholder}
        value={value}
        maxLength={maxLength}
        minLength={minLength}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
          e.stopPropagation();
          e.preventDefault();
          onChange(e.target.value);
        }}
        onPaste={onPaste}
        style={{
          background: isDisabled ? publicaColors.veryLightGray : publicaColors.white,
          border: `0.5px solid ${publicaColors.lightGray}`,
          borderRadius: borderRadius.small,
          boxShadow: isDisabled ? 'none' : 'inset 1px 2px 8px rgba(0, 0, 0, 0.05)',
          color: isDisabled ? publicaColors.gray : publicaColors.veryDarkGray,
          pointerEvents: isDisabled ? 'none' : 'all',
          fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
          padding: '6px 12px',
          width: '100%',
          ...(minHeight > 0 ? { minHeight } : {}),
          ...(isResizable ? {} : { resize: 'none' }),
          ...(sizeStyles[size] || {}),
          ...style,
        }}
      />
    </Div>
  );
};
